/**
 * PaieSubPage — Batch #14.250
 * Route : /admin/paie/:masterId/:tileId/:itemId?  (apercu · employes · periodes[/:runId] · bulletins · remises · ccq · reglages)
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { HandCoins, Loader2, ArrowLeft, AlertCircle } from "lucide-react";
import { toast } from "@/lib/toast";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { API, authH, YearSelect } from "./_shared";
import { PAIE_TILES } from "../PaieHub";
import OverviewTile from "./OverviewTile";
import EmployeesTile from "./EmployeesTile";
import RunsTile from "./RunsTile";
import RunDetail from "./RunDetail";
import PaystubsTile from "./PaystubsTile";
import RemittancesTile from "./RemittancesTile";
import CcqTile from "./CcqTile";
import SettingsTile from "./SettingsTile";
import YearEndTile from "./YearEndTile";
import RoeTile from "./RoeTile";
import RoeEditor from "./RoeEditor";

export default function PaieSubPage() {
  const { masterId, tileId, itemId } = useParams();
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/payroll/${masterId}/overview?year=${year}`, { headers: authH() });
      setOverview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement dossier");
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [masterId, year]);

  useEffect(() => { load(); }, [load]);

  const tile = PAIE_TILES.find((t) => t.id === tileId);
  const { setTrail } = useBreadcrumb();
  useEffect(() => {
    if (!tile) return undefined;
    const name = overview?.settings?.legal_name || "Dossier";
    setTrail([{ label: "Paie", href: "/admin/paie" }, { label: name, href: `/admin/paie?master_id=${masterId}` },
      ...(itemId ? [{ label: tile.title, href: `/admin/paie/${masterId}/${tileId}` }, { label: tileId === "releves-emploi" ? "Relevé d'emploi" : "Période" }] : [{ label: tile.title }])]);
    return () => setTrail(null);
  }, [tile, overview, masterId, tileId, itemId, setTrail]);
  const back = () => (itemId ? navigate(`/admin/paie/${masterId}/${tileId}`) : navigate(`/admin/paie?master_id=${masterId}`));
  const showYear = ["apercu", "periodes", "bulletins", "remises", "fin-annee"].includes(tileId) && !itemId;

  if (!tile || (!loading && !overview)) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white border-2 border-rose-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h2 className="text-base font-bold text-slate-800">{!tile ? "Module inconnu" : "Dossier inaccessible"}</h2>
          <button onClick={() => navigate("/admin/paie")} className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour au hub
          </button>
        </div>
      </div>
    );
  }

  const settings = overview?.settings || {};
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid={`paie-tile-${tileId}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={back} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition active:scale-90"
          aria-label="Retour" data-testid="paie-tile-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex items-center gap-2 text-sm text-slate-500 flex-1 min-w-0">
          <HandCoins className="w-4 h-4 text-orange-600" />
          <span className="font-medium truncate">{settings.legal_name || "Employeur"}</span>
          <span className="text-slate-300">›</span>
          <span className="font-bold text-slate-800">{tile.title}</span>
        </div>
        <div className="flex items-center gap-2">
          {showYear && (
            <>
              <span className="text-[11px] font-semibold text-slate-500 uppercase">Année</span>
              <YearSelect value={year} onChange={setYear} testId="paie-year-select" />
            </>
          )}
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
      </div>

      {overview && (
        <>
          {tileId === "apercu" && <OverviewTile masterId={masterId} overview={overview} />}
          {tileId === "employes" && <EmployeesTile masterId={masterId} settings={settings} onChanged={load} />}
          {tileId === "periodes" && !itemId && <RunsTile masterId={masterId} year={year} settings={settings} onChanged={load} />}
          {tileId === "periodes" && itemId && <RunDetail masterId={masterId} runId={itemId} settings={settings} onChanged={load} />}
          {tileId === "bulletins" && <PaystubsTile masterId={masterId} year={year} />}
          {tileId === "remises" && <RemittancesTile masterId={masterId} year={year} onChanged={load} />}
          {tileId === "ccq" && <CcqTile masterId={masterId} settings={settings} />}
          {tileId === "fin-annee" && <YearEndTile masterId={masterId} year={year} />}
          {tileId === "releves-emploi" && !itemId && <RoeTile masterId={masterId} />}
          {tileId === "releves-emploi" && itemId && <RoeEditor masterId={masterId} roeId={itemId} />}
          {tileId === "reglages" && <SettingsTile masterId={masterId} onChanged={load} />}
        </>
      )}
    </div>
  );
}
